import { combineReducers } from 'redux';
import produce from 'immer';
import { PackageAction, PackageActionType } from '../../actions/packageActions';

type UploadingPackage = { uploadingPackage: boolean; uploadingError: string };
type UploadPercentage = { percentCompleted: number };
type LoadingPackageDownloadLink = {
  loadingPackageDownloadLink: boolean;
  loadingPackageDownloadLinkError: string;
};
type LoadingUploadInfo = {
  loadingUploadInfo: boolean;
  loadingUploadInfoError: string;
};
type ProcessingState = { processing: boolean };

type PackageState = UploadingPackage &
  UploadPercentage &
  LoadingPackageDownloadLink &
  LoadingUploadInfo &
  ProcessingState;

interface PackageStatesByActivityTemplateId {
  [id: string]: Partial<PackageState>;
}

const ensureTemplateId = (draft: PackageStatesByActivityTemplateId, templateId: string) => {
  if (draft[templateId] === undefined) {
    draft[templateId] = {};
  }
};

const byId = (state: PackageStatesByActivityTemplateId = {}, action: PackageAction) =>
  produce(state, (draft) => {
    switch (action.type) {
      case PackageActionType.UPLOAD_PACKAGE: {
        ensureTemplateId(draft, action.id);
        draft[action.id].uploadingPackage = true;
        draft[action.id].uploadingError = '';
        draft[action.id].percentCompleted = 0;
        break;
      }
      case PackageActionType.UPLOAD_PACKAGE_OK: {
        ensureTemplateId(draft, action.id);
        draft[action.id].uploadingPackage = false;
        break;
      }
      case PackageActionType.UPLOAD_PACKAGE_ERROR: {
        ensureTemplateId(draft, action.id);
        draft[action.id].uploadingPackage = false;
        draft[action.id].uploadingError = action.error;
        break;
      }
      case PackageActionType.UPDATE_PACKAGE_UPLOAD_PROGRESS: {
        ensureTemplateId(draft, action.id);
        draft[action.id].percentCompleted = action.percentCompleted;
        break;
      }
      case PackageActionType.GET_PACKAGE_DOWNLOAD_LINK: {
        ensureTemplateId(draft, action.id);
        draft[action.id].loadingPackageDownloadLink = true;
        draft[action.id].loadingPackageDownloadLinkError = '';
        break;
      }
      case PackageActionType.GET_PACKAGE_DOWNLOAD_LINK_OK: {
        ensureTemplateId(draft, action.id);
        draft[action.id].loadingPackageDownloadLink = false;
        break;
      }
      case PackageActionType.GET_PACKAGE_DOWNLOAD_LINK_ERROR: {
        ensureTemplateId(draft, action.id);
        draft[action.id].loadingPackageDownloadLink = false;
        draft[action.id].loadingPackageDownloadLinkError = action.error;
        break;
      }
      case PackageActionType.GET_PACKAGE_UPLOAD_INFO: {
        ensureTemplateId(draft, action.id);
        draft[action.id].loadingUploadInfo = true;
        draft[action.id].loadingUploadInfoError = '';
        break;
      }
      case PackageActionType.GET_PACKAGE_UPLOAD_INFO_OK: {
        ensureTemplateId(draft, action.id);
        draft[action.id].loadingUploadInfo = false;
        break;
      }
      case PackageActionType.GET_PACKAGE_UPLOAD_INFO_ERROR: {
        ensureTemplateId(draft, action.id);
        draft[action.id].loadingUploadInfo = false;
        draft[action.id].loadingUploadInfoError = action.error;
        break;
      }
      case PackageActionType.HANDLE_GIT_ROBOT:
      case PackageActionType.PACKAGE_PROCESSING_START: {
        ensureTemplateId(draft, action.id);
        draft[action.id].processing = true;
        break;
      }
      case PackageActionType.HANDLE_GIT_ROBOT_OK:
      case PackageActionType.HANDLE_GIT_ROBOT_ERROR:
      case PackageActionType.PACKAGE_PROCESSING_ERROR:
      case PackageActionType.PACKAGE_BUILDING_PROGRESS:
      case PackageActionType.PACKAGE_PROCESSING_COMPLETED: {
        ensureTemplateId(draft, action.id);
        draft[action.id].processing = false;
        break;
      }
      default:
        return state;
    }
  });

export default combineReducers({
  byId,
});
